import colors, { getTextColor } from '../colors';
import { readableTextColor } from '../colorUtils';
import { WEEK_TIME_COLORS } from '../weekTimeBudget';

const HEX = /^#[0-9a-f]{6}$/i;

/** The account's own free time colour, as #RRGGBB, or null when it follows the accent (or holds something unknown). */
export const ownFreeTimeColor = (saved?: string | null) => (saved && HEX.test(saved) ? saved.toUpperCase() : null);

/**
 * The colours free study time is drawn in (Petr, 2026-10-15: a student's colour coding clashed with their accent).
 * Without a colour of the student's own, the calendar's free time is the accent's tint and the charts' (week donut,
 * Analytics, the Plan block slider) the donut's periwinkle, as before. With one, both take it, and the hours on the
 * calendar's free blocks are darkened or lightened just enough to read, as a pale pick would be lost on its own tint.
 * Missed sessions' stripes take it too (Petr): time that was planned and didn't happen, beside the free time it went
 * back to, in the donut, Analytics, Auto-plan's summary and on the calendar.
 */
export const freeTimeColors = (saved: string | null | undefined, background: string) => {
  const own = ownFreeTimeColor(saved);
  return {
    freeTimeColor: own ?? colors.primaryPurple,
    freeTimeTextColor: own ? readableTextColor(own, background, 3) : colors.primaryPurple,
    freeTimeChartColor: own ?? WEEK_TIME_COLORS.freeStudy,
    missedStudyColor: own ?? WEEK_TIME_COLORS.missedStudy
  };
};

/** The theme's colours for the donut's free study time and missed sessions (`freeTimeColors`). */
export type StudyTimeColors = { freeStudy: string; missedStudy: string };

export const studyTimeColorsOf = (theme: any): StudyTimeColors => ({
  freeStudy: theme?.freeTimeChartColor ?? WEEK_TIME_COLORS.freeStudy,
  missedStudy: theme?.missedStudyColor ?? WEEK_TIME_COLORS.missedStudy
});

/** The week donut's items with free study time and missed sessions in the theme's colours for them. */
export const withStudyTimeColors = <T extends { key: string; color: string }>(items: T[], colors: StudyTimeColors) =>
  items.map(item => {
    const color = item.key === 'freeStudy' || item.key === 'missedStudy' ? colors[item.key] : item.color;
    return color === item.color ? item : { ...item, color };
  });

/**
 * An Analytics bar type in the theme's colour when it is free study time or missed sessions. Missed sessions keep their
 * dark text, which reads on the pale half of their stripes whatever the colour.
 */
export const chartTypeInStudyTimeColors = <T extends { freeStudy?: boolean; missedStudy?: boolean; color: string }>(
  type: T,
  colors: StudyTimeColors
) => {
  if (type.freeStudy && colors.freeStudy !== type.color) {
    return { ...type, color: colors.freeStudy, borderColor: colors.freeStudy, textColor: getTextColor(colors.freeStudy) };
  }
  if (type.missedStudy && colors.missedStudy !== type.color) {
    return { ...type, color: colors.missedStudy, borderColor: colors.missedStudy };
  }
  return type;
};
